/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.kiloparsec

import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CompletableDeferred
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.isActive
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock
import net.sergeych.crypto2.SigningKey
import net.sergeych.crypto2.VerifyingKey
import net.sergeych.crypto2.VerifyingPublicKey
import net.sergeych.mp_logger.LogTag
import net.sergeych.mp_logger.Loggable
import net.sergeych.mp_logger.debug
import net.sergeych.mp_logger.exception
import net.sergeych.mp_tools.globalLaunch

/**
 * The auto-connecting client that reconnects to the kiloparsec server,
 * [KiloServer],
 * and maintain connection state flow. Client factory launches a disconnected
 * set of coroutines to support automatic reconnection, so you _must_ [close]
 * it manually when it is unnecessary, otherwise it will continue to reconnect.
 *
 * ## Usage
 *
 * Suppose we have TCP/IP server as in the [KiloServer] usage sample. Then we can connect
 * to it providing TCP/IP connector like:
 *
 * ```kotlin
 * val client = KiloClient<Unit>() {
 *     connect { connectTcpDevice("localhost:$port") }
 * }
 *
 * // now we can invoke remote commands:
 * assertEquals("unknown", client.call(cmdLoad))
 *
 * client.call(cmdSave, "foobar")
 * assertEquals("foobar", client.call(cmdLoad))
 * ```
 *
 * ## See also
 *
 * [KiloServer]
 *
 */
class KiloClient<S>(
    val localInterface: KiloInterface<S>,
    secretKey: SigningKey? = null,
    connectionDataFactory: ConnectionDataFactory<S>,
) : RemoteInterface,
    Loggable by LogTag("CLIF") {
    val _state = MutableStateFlow(false)

    /**
     * State flow that shows the current state of an auto-connecting client. Use it
     * to authenticate a client on connection restore, for example.
     */
    @Suppress("unused")
    val connectedStateFlow = _state.asStateFlow()

    /**
     * The verifying, or public, key identifying client sessions. It could be used to
     * restore environment on reconnection. This is what remote side, e.g. server, sees as
     * [KiloScope.remoteIdentity].
     */
    @Suppress("unused")
    val localIdentity: VerifyingKey? = secretKey?.verifyingKey

    private var deferredClient = CompletableDeferred<KiloClientConnection<S>>()

    private val job =
        globalLaunch {
            debug { "starting connector" }
            while (isActive) {
                try {
                    debug { "getting connection" }
                    val kc = connectionDataFactory()
                    debug { "get device and session" }
                    val client = KiloClientConnection(localInterface, kc, secretKey)
                    deferredClient.complete(client)
                    debug { "starting client run"}
                    val r = runCatching {  client.run {
                        _state.value = it
                    } }
                    debug { "----------- client run finished: $r" }
                    resetDeferredClient()
                    debug { "client run finished" }
                } catch (_: RemoteInterface.ClosedException) {
                    debug { "remote closed" }
                    delay(1000)
                } catch (_: CancellationException) {
                    debug { "cancelled" }
                    break
                } catch (t: Throwable) {
                    exception { "unexpected exception" to t }
                    delay(1000)
                }
                _state.value = false
                resetDeferredClient()
                // reconnection timeout
                delay(700)
            }
        }

    fun close() {
        job.cancel()
        debug { "client is closed" }
    }

    private val defMutex = Mutex()
    private suspend fun resetDeferredClient() {
        defMutex.withLock {
            if (!deferredClient.isActive) {
                deferredClient = CompletableDeferred()
            }

        }
    }

    override suspend fun <A, R> call(cmd: Command<A, R>, args: A): R =
        try {
            deferredClient.await().call(cmd, args)
        } catch (t: RemoteInterface.ClosedException) {
            resetDeferredClient()
            throw t
        }

    override suspend fun <A> push(cmd: Command<A, Unit>, args: A) {
        try {
            deferredClient.await().push(cmd, args)
        } catch (t: RemoteInterface.ClosedException) {
            resetDeferredClient()
            throw t
        }
    }

    /**
     * Current session token. This is a per-connection unique random value same on the client and server part so
     * it could be used as a nonce to pair MITM and like attacks, be sure that the server is actually
     * working, etc.
     */
    suspend fun token() = deferredClient.await().token()

    /**
     * Remote party shared key ([VerifyingPublicKey]), could be used ti ensure server is what we expected and
     * there is no active MITM attack.
     *
     * Non-null value means the key was successfully authenticated, null means remote party did not provide
     * a key. Connection is established either with a properly authenticated key or no key at all.
     */
    @Suppress("unused")
    suspend fun remoteIdentity(): VerifyingPublicKey? = deferredClient.await().remoteId()

    companion object {
        class Builder<S>() {

            private var interfaceBuilder: (KiloInterface<S>.() -> Unit)? = null
            private var sessionBuilder: (() -> S) = {
                @Suppress("UNCHECKED_CAST")
                Unit as S
            }
            private var connectionBuilder: (suspend () -> Transport.Device)? = null

            var secretIdKey: SigningKey? = null

            /**
             * Build local command implementations, those callable from the server, exception
             * class handlers, and anything else [KiloInterface] allows. Usage sample:
             * 
             * ```kotlin
             * val client = KiloClient {
             *     connect { connectTcpDevice("localhost:$port") }
             *     local {
             *         on(cmdPing) {
             *             "pong! $it"
             *         }
             *     }
             * }
             * ```
             */
            fun local(f: KiloInterface<S>.() -> Unit) {
                interfaceBuilder = f
            }

            /**
             * Import exception (error) registrations from a shared LocalInterface (usually, server side one) to
             * DRY error registration on both server and client sides.
             * Normally you need to:
             *
             * - Register all your errors in server's LocalInterface
             *
             * - In the client builder call [addErrors(serverLocalInterface]
             *
             * See also [LocalInterface.registerError] for exception registration
             */
            fun addErrors(from: LocalInterface<*>) {
                errorProviders += from
            }

            /**
             * Create a new session object, otherwise Unit session will be used
             */
            fun session(f: () -> S) {
                sessionBuilder = f
            }

            fun connect(f: suspend () -> Transport.Device) {
                connectionBuilder = f
            }

            val errorProviders = mutableListOf<LocalInterface<*>>()

            internal fun build(): KiloClient<S> {
                val i = KiloInterface<S>()
                for (ep in errorProviders) i.addErrorProvider(ep)
                interfaceBuilder?.let { i.it() }
                val connector = connectionBuilder ?: throw IllegalArgumentException("connect handler was not set")
                return KiloClient(i, secretIdKey) {
                    KiloConnectionData(connector(), sessionBuilder())
                }
            }
        }

        /**
         * Call the secure remote command when the secure connection is established. Note that
         * it might fail on disconnect. We do not automatically repeat command on disconnect
         * as actual services might need identification on reconnecting.
         */
        operator fun <S> invoke(f: Builder<S>.() -> Unit): KiloClient<S> {
            return Builder<S>().also { it.f() }.build()
        }
    }
}
